/**
 * ProviderMark — self-contained inline-SVG provider marks (Story MC6).
 *
 * Hand-rolled SVG (NO `<img src>`, NO external URL, NO data-URI fetch) so the
 * app stays CSP-safe + offline-safe by construction and mock-mode tests stay
 * hermetic (zero network). Wrapped in an 8px logo tile (design.md §4).
 *
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §4 — logo tiles 8px]
 */

import { cn } from '../../../lib/cn';
import type { MailboxProvider } from '../types';

const PROVIDER_LABEL: Record<MailboxProvider, string> = {
  microsoft: 'Microsoft 365 / Outlook',
  google: 'Google Workspace / Gmail',
};

/** Short brand label helper (used by the provider cards + the connection rows). */
export function providerLabel(provider: MailboxProvider): string {
  return PROVIDER_LABEL[provider];
}

/** Microsoft 4-square mark (#F25022 / #7FBA00 / #00A4EF / #FFB900). */
function MicrosoftSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

/** Google 4-color "G" mark. */
function GoogleSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.83Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.94-2.9l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.29 14.3a7.2 7.2 0 0 1 0-4.6v-3.1H1.28a12 12 0 0 0 0 10.8l4.01-3.1Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.44-3.44C17.95 1.17 15.24 0 12 0A12 12 0 0 0 1.28 6.6l4.01 3.1C6.23 6.86 8.88 4.75 12 4.75Z"
      />
    </svg>
  );
}

export function ProviderMark({
  provider,
  className,
}: {
  provider: MailboxProvider;
  className?: string;
}) {
  const Svg = provider === 'microsoft' ? MicrosoftSvg : GoogleSvg;
  return (
    <span
      role="img"
      aria-label={PROVIDER_LABEL[provider]}
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-card',
        className,
      )}
    >
      <Svg className="size-5" />
    </span>
  );
}
